<template>
  <b-card no-body class="contacts-card" v-if="contact" :contact-id="contact.id">
    <b-card-header v-if="referralsLabel" class="text-center mb-3 primary-background-color cursor-pointer text-white" v-b-modal="modalID">
      {{referralsLabel}}
      <span v-b-tooltip.hover :title="$t('ctas.learn-more')">
        <font-awesome-icon icon="info-circle"/>
      </span>
    </b-card-header>
    <b-card-header v-else class="mb-3 cursor-pointer">&nbsp;</b-card-header>

    <b-card-body class="contact-card-body my-1" v-b-modal="modalID">
      <b-row class="d-flex flex-column justify-content-center align-items-center cursor-pointer">
        <no-ssr>
          <radial-progress-bar class="mb-2" :diameter="80" :stroke-width="5" :inner-stroke-width="5"
            start-color="#0083ff" stop-color="#0083ff" inner-stroke-color="#E5E5E5"
            :completed-steps="this.relevance > 0 ? Math.round(this.relevance) : 0" :total-steps="100">
              <img :src="`/static/images/avatars/main${contact.avatar || 1}.png`" class="contact-card-picture"/>
          </radial-progress-bar>
          <div class="match-label" v-if="matchStatus" :class="matchColorClass">{{ $t('contacts.card.match-label') }}</div>
          <div class="match-label" v-else>&nbsp;</div>
        </no-ssr>
        <template v-if="isRecruiter">
          <div class="current-workplace workplace py-3" v-if="contact.latestEmployment">
            <div class="standard-black-title">{{ shortJobTitle || '&nbsp;' }}</div>
            <i18n path="contacts.card.currently-at" tag="div" class="company secondary-color">
              <template v-slot:companyName><strong>{{ contact.latestEmployment.companyName }}</strong></template>
            </i18n>
            <div class="time secondary-color">{{ contact.latestEmployment.startDate ? `${formatDate(contact.latestEmployment.startDate, 'YYYY')} -` : '' }}</div>
            <div class="company" v-if="location"><b>{{location}}</b></div>
          </div>
        </template>
        <template v-else>
          <div class="current-workplace workplace py-3">
            <div class="standard-black-title">{{ contact.fullName }}</div>
            <template v-if="contact.latestEmployment">
              <div class="standard-black-title">{{ shortJobTitle || '&nbsp;' }}</div>
              <i18n path="contacts.card.currently-at" tag="div" class="company secondary-color">
                <template v-slot:companyName><strong>{{ contact.latestEmployment.companyName }}</strong></template>
              </i18n>
              <div class="time secondary-color">{{ contact.latestEmployment.startDate ? `${formatDate(contact.latestEmployment.startDate, 'YYYY')} -` : '' }}</div>
              <div class="company" v-if="location"><b>{{location}}</b></div>
            </template>
          </div>
        </template>
      </b-row>
    </b-card-body>

    <b-card-footer class="border-0 pt-0"><slot name="actions" v-bind="{ contact }"/></b-card-footer>

    <b-card-footer v-b-modal="modalID">
      <h4><a href="#" v-on:click.prevent>{{ $t('ctas.view-profile') }}</a></h4>
    </b-card-footer>
    <contact-modal :contact="contact" :id="modalID"/>
  </b-card>
</template>

<script>
import ContactModal from '@/modals/contact-profile';
import formatDate from 'date-fns/format';
import enumsMixin from '@src/mixins/enums';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'FilterContactsCard',
  props: {
    contact: {
      type: Object,
      validate(contact) {
        return contact && contact.id;
      },
    },
    edge: {
      type: Object,
      default: () => null,
    },
    relevance: {
      type: Number,
      default: () => 0,
    },
  },
  components: {
    ContactModal,
    RadialProgressBar: () => import('vue-radial-progress'),
  },
  mixins: [
    enumsMixin,
  ],
  computed: {
    ...mapRoot([ 'user', 'isRecruiter', 'isSainsburys' ]),
    shortJobTitle() {
      if (this.contact.latestEmployment.title && this.contact.latestEmployment.title.length >= 50) {
        return `${this.contact.latestEmployment.title.substring(0, 46)}...`;
      } else {
        return this.contact.latestEmployment.title;
      }
    },
    previous() {
      if (this.contact.previousEmployment.length) {
        return this.contact.previousEmployment[0];
      } else {
        return null;
      }
    },
    location() {
      const { locations } = this.contact || {};
      if (Array.isArray(locations)) {
        const { city, country } = locations.find(l => l && l.isPrimary && (l.city || l.country)) ||
          locations.find(l => l && (l.city || l.country)) || {};
        return (city || country) ? `${city || ''}${city && country ? ', ' : ''}${country || ''}`.trim() : null;
      } else {
        return null;
      }
    },
    matchStatus() {
      const { STRONG, VERY_STRONG } = this.contactRelevanceBoundaries;

      if (this.relevance >= VERY_STRONG) {
        return 'VERY_STRONG';
      } else if (this.relevance >= STRONG) {
        return 'STRONG';
      }

      return null;
    },
    matchColorClass() {
      if (this.matchStatus === 'WEAK') {
        return 'text-danger';
      } else if (this.matchStatus === 'VERY_STRONG') {
        return 'text-primary bg-primary-a2';
      } else if (this.matchStatus === 'STRONG') {
        return 'text-secondary bg-primary-a2';
      } else if (this.matchStatus === 'AVERAGE') {
        return 'text-warning';
      } else {
        return null;
      }
    },
    modalID() {
      return `contact-modal-${this.contact.id}`;
    },
    referralsLabel() {
      const referrals = this.contact && Array.isArray(this.contact.referrals) ? this.contact.referrals : [];

      if (referrals.find(r => r && r.status === 'ACCEPTED')) {
        if (this.$route && this.$route.name === 'JobShow' && this.$route.params && this.$route.params.jobID) {
          const { id: userID } = this.user;
          const { jobID } = this.$route.params;

          if (referrals.find(r => r && r.job && r.job.id === jobID && r.user && r.user.id === userID)) {
            return this.$t('contacts.card.you-already-referred-job');
          }
          if (referrals.find(r => r && r.job && r.job.id === jobID)) {
            return this.$t('contacts.card.someone-already-referred-job');
          }
        } else {
          return this.$t('contacts.card.someone-already-referred-company');
        }
      }

      if (this.isRecruiter && referrals.find(r => r && r.status === 'REQUESTED')) {
        return this.$t('contacts.card.recruiter-already-referred-requested');
      }

      return null;
    }
  },
  methods: {
    formatDate,
  },
};
</script>
<style lang="scss">
@import './../../scss/variables';

.contacts-card {
  .card-body {
    padding-left: 2rem;
    padding-right: 2rem;
    &.contact-card-body {
      padding-top: 0rem;
    }
    padding-bottom: 0px;
  }
  .card-header {
    margin-bottom: 0.75em;
    background-color: white;
    border-style: none;
  }
  .contact-card-picture {
    border-radius: 50%;
  }
  .card-footer {
    background-color: white;
    text-align: center;
    h4 {
      margin-bottom: 0px;
      a {
        font-weight: 500;
      }
    }
  }
  .circle, .match-label, .btn, .dropdown, .workplace {
    margin: 0 auto;
  }
  .match-label {
    padding: 0 4px 0 4px;
  }
  .workplaces {
    border-top: 1px solid rgba(0, 0, 0, 0.125);
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: nowrap;
  }
  .workplace {
    font-size: $small-font-size;
    text-align: center;
    padding: 1.2rem 0 0.7rem 0;
    // text-transform: capitalize;
    &.current-workplace {
      padding-top: 0px;
      padding-bottom: 0px;
      overflow-y: hidden;
    }
  }

  .btn, .dropdown {
    padding-bottom: 0.3rem;
    width: 100%;
  }
  .row {
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/contacts-filter/card.vue